import React, { useState, useCallback, useRef, useEffect, useContext } from "react";
import {FormContext} from "@proxy/_contexts";
import OverlayTop from "./overlays/OverlayTop";
import OverlayMiddle from "./overlays/OverlayMiddle";
import OverlayBottom from "./overlays/OverlayBottom";

window.recordedBlobs = [];

export default function VideoRecorder() {

  const {
    formData,
    enableCamera,
    closeCameraModal,
    handleRecordingComplete,
    updateFormField,
  } = useContext(FormContext);

  const timeAvailable = navigator.vendor.includes("Apple") ? 120 : 120;
  const videoPlayer = useRef(null);
  document.getElementById("gc__video_stream")?.removeAttribute('controls');

  const [loading, setLoading] = useState(false);
  const [overlayState, setOverlayState] = useState("playback-complete");
  const [cameraStream, setCameraStream] = useState(null);
  const [videoRecorder, setVideoRecorder] = useState(null);
  const [autoplay, setAutoplay] = useState(true);
  const [error, setError] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(timeAvailable);
  const [videoDuration, setVideoDuration] = useState(null);
  const [hasThumbnail, setHasThumbnail] = useState(false);
  const [recordedUrl, setRecordedUrl] = useState(null);

  useEffect(() => {
    if (formData.properties._gcp_video_id != "") {
      videoPlayer.current.src = formData.properties._gcp_video_id;
      videoPlayer.current.onended = () => {
        setOverlayState("playback-complete");
      }
    }
  }, []);

  /**
   * The useEffect will check to see if the enableCamera flag has changed.
   * If the flag is true, then startCamera will be called.
   * If the flag is false, then stopCamera will be called.
   */
  useEffect(() => {
    if (enableCamera) {
      startCamera();
    } else {
      stopCamera();
    }
  }, [enableCamera]);


  /**
   * The useEffect will check to see if timeRemaining or videoRecorder have changed.
   * While recording, a timer will be displayed and countdown.  Upon reaching 0
   * seconds stopRecord will be called.
   */
  useEffect(() => {
    if ( timeRemaining > 0 && isRecording() ) {
      setTimeout(() => setTimeRemaining(timeRemaining - 1), 1000);
    } else if ( timeRemaining <= 0 ) {
      stopRecord();
    }
  }, [timeRemaining, videoRecorder]);

  /**
   * Formats a given number of seconds in mm:ss format.
   *
   * @param { int } totalSeconds -> The number of seconds to format.
   *
   * @return { string } -> The formatted time.
   */
  const formatTimeRemaining = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    let seconds = totalSeconds % 60;
    if ( Math.floor( seconds / 10 ) <= 0 ) seconds = "0" + seconds;
    return (minutes + ":" + seconds);
  }

  /**
   * check whether the camera is currently streaming or not
   * @returns boolean
  */
  const isCameraStreaming = () => {
    return (cameraStream && cameraStream.active);
  }

  /**
   * check whether the reconding is currently in progress
   * @returns boolean
   */
  const isRecording = () => {
    return (videoRecorder && videoRecorder.state === 'recording');
  }

  /**
   * initialize the user camera and starts it
   * change the state of the cameraStream to that
   * set the video source of the videoPlayer to the cameraStream source
   * through an error if the permission to access camera is denied
   */
  const startCamera = useCallback(() => {
    setLoading(true);
    setOverlayState("ready");
    setTimeRemaining(timeAvailable);
    navigator.mediaDevices.getUserMedia({ video: { width: 800, height: 450 }, audio: true })
      .then((mediaStream) => {
        setAutoplay(true);
        setCameraStream(mediaStream);
        videoPlayer.current.srcObject = mediaStream;
        videoPlayer.current.muted = "muted";
        videoPlayer.current.onended = () => {
          setOverlayState("playback-complete");
        }
        setLoading(false);
      })
      .catch(err => {
        setError(err);
      })
  }, [cameraStream]);

  /**
   * stop the user camera from streaming
   * also reset the cameraStream and videoPlayer source to null
   */
  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
    }
    setAutoplay(false);
    setCameraStream(null);
    videoPlayer.current.srcObject = null;
  }

  /**
   * handle what happens after the recording is stopped
   * get all the recorded blobs from the recordedBlobs array and convert to a Blob instance
   * use the URL api to extract a url to the object
   * return the url to the object to the handleRecordingComplete callback function
   */
  const handleAfterRecordingStop = () => {

    if (typeof(window.recordedBlobs[0]) == 'undefined') {
      console.warn("Nothing has been recorded as of yet", window.recordedBlobs);
      return false;
    }
    const recordedObj = new Blob(window.recordedBlobs, { type: window.recordedBlobs[0].type });
    const url = URL.createObjectURL(recordedObj);


    videoPlayer.current.autoPlay = false;
    videoPlayer.current.controls = true;
    videoPlayer.current.src      = url;

    setAutoplay(false);
    setRecordedUrl(url);
    window.recordedBlobs = [];

  };

  /**
   * starts the recording
   * push the blobs to the recordedBlobs array when available
   * handle what happen after stop when stopped
   */
  const startRecord = () => {
    setOverlayState("recording");
    // const fileExtension = navigator.vendor.includes("Apple") ? "mp4" : "webm";
    // const mediaRecorder = new MediaRecorder(cameraStream, { mimeType: `video/${fileExtension}` });
    const options = {
      audioBitsPerSecond : 128000,
      videoBitsPerSecond : 125000,
    };

    const mediaRecorder = new MediaRecorder(cameraStream);
    setVideoRecorder(mediaRecorder);

    videoPlayer.current.muted = "muted";

    mediaRecorder.start();
    setTimeRemaining(timeAvailable)

    mediaRecorder.ondataavailable = async (e) => {

      const newBlobs = [...window.recordedBlobs];

      if (!e.data || !e.data.size || e.data.size === 0) {
        return false;
      }
      newBlobs.push(e.data);

      window.recordedBlobs = newBlobs;
    }

    mediaRecorder.onstop = () => {
      handleAfterRecordingStop();
    }
  }

  /**
   * stops the recording
   */
  const stopRecord = () => {
    stopCamera();
    videoPlayer.current.muted = null;
    videoRecorder.stop();
    setVideoDuration(timeAvailable - timeRemaining);
    setOverlayState("playback-complete");
  }

  /**
   * callback to play the recording when play recording onClick
   */
  const playRecording = () => {
    videoPlayer.current.muted = null;
    videoPlayer.current.play();
  }

  /**
   * callback to pause the recording when pause recording onClick
   */
  const pauseRecording = () => {
    videoPlayer.current.muted = null;
    videoPlayer.current.pause();
  }

  /**
   * create and return the video player element depending on the conditions
   * @returns video player element jsx
   */
  const videoPlayerElement = () => {
    if (!autoplay) {
      return (
        <video ref={videoPlayer} id="gc__video_stream"></video>
      )
    } else {
      return (
        <video ref={videoPlayer} id="gc__video_stream" autoPlay></video>
      )
    }
  }

  if (error) {
    console.log(error);
  }

  return (
    <div className="gc__video_recorder_container">
      <div className="gc__video_player">
        {videoPlayerElement()}
        <div className={"gc__overlay_wrapper"}>
          <OverlayTop
            overlayState={overlayState}
            setOverlayState={setOverlayState}
            closeModal={closeCameraModal}
            stopCamera={stopCamera}
          />

          <OverlayMiddle
            loading={error ? false : loading}
            overlayState={error ? "error" : overlayState}
            setOverlayState={setOverlayState}
            isRecording={isRecording()}
            startCamera={startCamera}
            closeModal={closeCameraModal}
            media={document.getElementById('gc__video_stream')}
            videoDuration={videoDuration}
            updateFormField={updateFormField}
            formData={formData}
            playRecording={playRecording}
            setHasThumbnail={setHasThumbnail}
          />

          <OverlayBottom
            handleRecordingComplete={handleRecordingComplete}
            overlayState={error ? "error" : overlayState}
            setOverlayState={setOverlayState}
            isRecording={isRecording()}
            isCameraStreaming={isCameraStreaming()}
            timeRemaining={formatTimeRemaining(timeRemaining)}
            startRecord={startRecord}
            stopRecord={stopRecord}
            playRecording={playRecording}
            pauseRecording={pauseRecording}
            closeModal={closeCameraModal}
            updateFormField={updateFormField}
            formData={formData}
            hasThumbnail={hasThumbnail}
            recordedUrl={recordedUrl}
          />
        </div>
      </div>
    </div>
  )
}
